import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ResultsCard = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  text-align: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 20px;
`;

const ScoreDisplay = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: #4caf50;
  margin: 20px 0;
`;

const ResultText = styled.p`
  font-size: 1.2rem;
  color: #555;
  margin-bottom: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
`;

const Button = styled(Link)`
  background-color: ${props => props.primary ? '#4caf50' : '#2196f3'};
  color: white;
  padding: 12px 20px;
  font-size: 1.1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${props => props.primary ? '#45a049' : '#0b7dda'};
  }
`;

const CountryList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin: 20px 0;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  text-align: left;
`;

const CountryItem = styled.div`
  padding: 5px 0;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
`;

const Results = () => {
  const [results, setResults] = useState({
    score: 0,
    total: 0,
    lives: 0,
    settings: {
      gameMode: 'standard',
      numberOfQuestions: 10,
      timeLimit: 30,
      lives: 3,
      difficulty: 'medium',
      marathonTimeLimit: 300,
      infiniteTime: false,
      useAllCountries: false
    },
    completedCountries: [],
    timeRemaining: 0,
    elapsedTime: 0
  });

  useEffect(() => {
    const savedResults = localStorage.getItem('quizResults');
    if (savedResults) {
      setResults(JSON.parse(savedResults));
    }
  }, []);

  const getPerformanceMessage = () => {
    const percentage = (results.score / results.total) * 100;
    
    if (percentage >= 90) {
      return "Outstanding! You're a flag expert!";
    } else if (percentage >= 70) {
      return "Great job! You know your flags well!";
    } else if (percentage >= 50) {
      return "Good effort! You have a decent knowledge of flags.";
    } else if (percentage >= 30) {
      return "Not bad! Keep learning and you'll improve.";
    } else {
      return "You might want to study some flags. Keep practicing!";
    }
  };

  const formatTime = (seconds) => {
    if (seconds === Infinity) return 'Infinite';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <ResultsContainer>
      <ResultsCard>
        <Title>Quiz Results</Title>
        
        <ScoreDisplay>
          {results.score} / {results.total}
        </ScoreDisplay>
        
        <ResultText>
          {getPerformanceMessage()}
        </ResultText>
        
        <ResultText>
          Game Mode: {results.settings.gameMode === 'standard' ? 'Standard' : 'Marathon'}
        </ResultText>
        
        <ResultText>
          Difficulty: {results.settings.difficulty.charAt(0).toUpperCase() + results.settings.difficulty.slice(1)}
        </ResultText>
        
        {results.settings.gameMode === 'standard' ? (
          // Standard mode specific results
          <>
            <ResultText>
              Time per question: {results.settings.timeLimit} seconds
            </ResultText>
            
            <ResultText>
              Lives remaining: {results.lives} out of {results.settings.lives}
            </ResultText>
          </>
        ) : (
          // Marathon mode specific results
          <>
            <ResultText>
              Time setting: {results.settings.infiniteTime ? 'Infinite' : `${results.settings.marathonTimeLimit} seconds`}
            </ResultText>
            
            <ResultText>
              Time taken: {formatTime(results.elapsedTime)}
            </ResultText>
            
            {!results.settings.infiniteTime && (
              <ResultText>
                Time remaining: {formatTime(results.timeRemaining)}
              </ResultText>
            )}
            
            <ResultText>
              Countries identified: {results.completedCountries.length}
            </ResultText>
            
            {results.completedCountries.length > 0 && (
              <>
                <ResultText>Countries you identified correctly:</ResultText>
                <CountryList>
                  {results.completedCountries.map((country, index) => (
                    <CountryItem key={index}>{country.name}</CountryItem>
                  ))}
                </CountryList>
              </>
            )}
          </>
        )}
        
        <ButtonContainer>
          <Button to="/settings">Play Again</Button>
          <Button to="/" primary>Home</Button>
        </ButtonContainer>
      </ResultsCard>
    </ResultsContainer>
  );
};

export default Results;
